import type {
  NotificationType,
  UserNotification,
} from '@/lib/types/notifications.types';

type NotificationNavigationSource = 'notifications';

type NotificationRouteTarget =
  | {
      to: '/events/$eventId';
      params: { eventId: string };
      search: { notificationId: string; from?: NotificationNavigationSource };
    }
  | {
      to: '/feed/$postId';
      params: { postId: string };
      search: { notificationId: string };
    }
  | {
      to: '/admin/moderate-posts';
      search: { notificationId: string };
    };

export const NOTIFICATION_EXPAND_THRESHOLD = 160;

export function getNotificationTypeLabel(type: NotificationType): string {
  switch (type) {
    case 'event_created':
      return 'New Event';
    case 'event_change':
      return 'Event Update';
    case 'event_cancelled':
      return 'Cancelled';
    case 'remind_3_days':
      return '3-Day Reminder';
    case 'remind_1_day':
      return '1-Day Reminder';
    case 'remind_1_hr':
      return '1-Hour Reminder';
    case 'post_comment':
      return 'Comment';
    case 'post_approved':
      return 'Approved';
    case 'post_rejected':
      return 'Not Approved';
    case 'post_pending':
      return 'Admin Review';
  }
}

export function getNotificationPillVariant(type: NotificationType) {
  switch (type) {
    case 'event_cancelled':
    case 'post_rejected':
      return 'error' as const;
    case 'event_created':
    case 'post_approved':
      return 'success' as const;
    case 'post_pending':
      return 'warning' as const;
    case 'remind_3_days':
    case 'remind_1_day':
    case 'remind_1_hr':
      return 'secondary' as const;
    case 'event_change':
    case 'post_comment':
      return 'primary' as const;
  }
}

export function getNotificationPreview(
  body: string,
  maxLength: number = 120
): string {
  if (body.length <= maxLength) {
    return body;
  }

  return `${body.slice(0, maxLength - 1).trimEnd()}…`;
}

export function shouldShowNotificationExpand(
  body: string,
  maxLength: number = NOTIFICATION_EXPAND_THRESHOLD
): boolean {
  return body.length > maxLength;
}

export function formatNotificationDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function getNotificationCtaLabel(
  notification: UserNotification
): string {
  switch (notification.notification_type) {
    case 'post_comment':
      return 'View conversation';
    case 'post_pending':
      return 'Review pending posts';
    default:
      switch (notification.destination_kind) {
        case 'event':
          return 'View event';
        case 'post':
          return 'View post';
        case 'admin_queue':
          return 'Open admin queue';
      }
  }
}

export function getNotificationUnavailableMessage(
  notification: UserNotification
): string {
  switch (notification.destination_kind) {
    case 'event':
      return 'This event is no longer available.';
    case 'post':
      return 'This post is no longer available.';
    case 'admin_queue':
      return 'This admin destination is no longer available for your account.';
  }
}

export function getNotificationRouteTarget(
  notification: UserNotification,
  source?: NotificationNavigationSource
): NotificationRouteTarget | null {
  switch (notification.destination_kind) {
    case 'event':
      if (!notification.event_id) {
        return null;
      }

      return {
        to: '/events/$eventId',
        params: { eventId: notification.event_id.toString() },
        search: {
          notificationId: notification.id,
          ...(source ? { from: source } : {}),
        },
      };
    case 'post':
      if (!notification.post_id) {
        return null;
      }

      return {
        to: '/feed/$postId',
        params: { postId: notification.post_id.toString() },
        search: { notificationId: notification.id },
      };
    case 'admin_queue':
      return {
        to: '/admin/moderate-posts',
        search: { notificationId: notification.id },
      };
    default: {
      const _exhaustiveCheck: never = notification.destination_kind;
      return _exhaustiveCheck;
    }
  }
}
